import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import AccountPicklist
  from 'Components/ComponentLibrary/Organisms/UpNextTaskingPicker/UpNextTasking/ActionItems/ActionItemList/Content/ActionItem/ListItem/AccountPicklist';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box/Box';
import Button1 from 'Components/ComponentLibrary/Components/Buttons/Button1';
import { CheckCircle, Description } from '@material-ui/icons';
import Uploader from 'Components/Files/GenericUploader/Uploader';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import LinearProgress from 'Components/UI/LinearProgress';
import { Tooltip as MuiTooltip, withStyles } from '@material-ui/core';
import useUserContext from 'Context/UserContext';
import EditIcon from '@material-ui/icons/Edit';
import { addQuery } from 'Helpers/Query';
import { buildModalQuery } from 'Consts/ModalQueries';

const styles = theme => ({
  root: {},
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  actionButton: {
    fontWeight: theme.typography.fontWeightBold,
  },
  accountButton: {
    width: '200px',
  },
  actionButtonSection: {
    padding: theme.spacing(0, 1),
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    '&:last-child': {
      border: 'none',
    },
  },
  progressBar: {
    maxHeight: 24,
    minWidth: 100,
    '& .MuiLinearProgress-root': {
      borderRadius: theme.radius.lg,
    },
  },
  [theme.breakpoints.down('md')]: {
    root: {
      justifyContent: 'flex-start',
    },
  },
});

const Tooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
  },
  arrow: {
    color: theme.palette.facet.accentBlue,
  },
}))(MuiTooltip);

const DefaultActions = ({
  className,
  classes,
  resource,
  fileLinkCollection,
  setFileLinkCollection,
  setProgress,
  selectedAccountId,
  setSelectedAccountId,
  toggleHide,
  hasAccounts,
  uploadProgress,
  completed,
  markAsComplete,
  showMarkCompleteTooltip,
}) => {
  // todo this is copy/paste from the action item list item. this needs a lot of clean up.
  // todo there may be functionality here that we no longer need.
  const { isAgent } = useUserContext();
  const hidden = resource.attr('hidden') === 'true';
  const userCanUpdateActionItem = resource.attr('isReadOnly') !== 'true';
  const fileUploadUrl = resource.relationship('uploadSecureFiles')
    ? resource
      .relationship('uploadSecureFiles')
      .link('related')
      .attr('url')
    : '';
  const disableAddFileButton = selectedAccountId === undefined || selectedAccountId < 1;

  let componentPicklist = null;
  if (hasAccounts === null) {
    componentPicklist = <div />;
  } else if (hasAccounts) {
    componentPicklist = (
      <AccountPicklist
        onSelect={accountID => setSelectedAccountId(accountID)}
      />);
  }
  const addFileButtonLabel = 'Add File';

  const addFileButton = disableAddFileButton ? (
    //  Wallace 1/19/2021 - The add file button should only be disable-able for voided checks.
    <Tooltip title="Select an account in order to add a file.">
      <Box className={classes.actionButtonSection}>
        <Button1
          className={classes.actionButton}
          startIcon={<Description />}
          color={'primary'}
          size={'small'}
          disableElevation
          hidden={completed || !userCanUpdateActionItem}
          disabled
        >
          {addFileButtonLabel}
        </Button1>
      </Box>
    </Tooltip>
  ) : (
    <Box className={classes.actionButtonSection}>
      <Button1
        className={classes.actionButton}
        startIcon={<Description />}
        color={'primary'}
        size={'small'}
        hidden={completed || !userCanUpdateActionItem}
      >
        {addFileButtonLabel}
      </Button1>
    </Box>
  );

  const ComponentFileUpload = !fileUploadUrl ? null : (
    <Uploader
      url={fileUploadUrl}
      setUploadProgress={(v) => {
        setProgress(v);
      }}
      onSuccess={() => fileLinkCollection.refresh().then((coll) => {
        setFileLinkCollection(coll);
      }).catch((err) => {
        displayErrorMessage(err,
          'Failed to refresh files, please try refreshing the page.');
      })
      }
      successToastMessage="Successfully uploaded file(s)"
    >
      {uploadProgress ? (
        <LinearProgress
          color="accentGreen"
          className={classes.progressBar}
          visible
          text={uploadProgress === 100 ? 'Processing...' : ''}
          textVariant="body1"
          ProgressProps={{
            variant: 'determinate',
            value: uploadProgress,
          }}
        />
      ) : (addFileButton)}
    </Uploader>
  );

  return (
    <div className={classNames(classes.root, classes.actions, className)}>
      <div className={classes.actions}>
        { componentPicklist }
        <Box className={classNames({
          [classes.actionButtonSection]: completed && userCanUpdateActionItem,
        })}
        >
          <Button1
            className={classes.actionButton}
            color={'primary'}
            size={'small'}
            disableElevation
            onClick={toggleHide}
            hidden={!completed || !userCanUpdateActionItem}
          >
            {hidden ? 'Un-hide' : 'Hide'}
          </Button1>
        </Box>
        { ComponentFileUpload }
        <Box className={classes.actionButtonSection}>
          <Button1
            startIcon={<EditIcon />}
            className={classes.actionButton}
            color={'primary'}
            size={'small'}
            disableElevation
            onClick={() => {
              if (isAgent) {
                addQuery({ todoId: resource.id() });
                openModal(modalConsts.simpleActionItemModal);
              } else {
                openModal(
                  buildModalQuery(modalConsts.clientActionItemModal, {
                    actionItemId: resource.id(),
                  }),
                );
              }
            }}
            hidden={completed || !userCanUpdateActionItem}
          >
            {'Edit'}
          </Button1>
        </Box>
        <Tooltip
          arrow
          open={showMarkCompleteTooltip}
          title="Did you want to mark this as complete?"
        >
          <Box className={classes.actionButtonSection}>
            <Button1
              disabled={completed}
              startIcon={completed ? <CheckCircle /> : undefined}
              className={classes.actionButton}
              color={'primary'}
              size={'small'}
              disableElevation
              onClick={markAsComplete}
              hidden={!completed && !userCanUpdateActionItem}
            >
              {completed ? 'Completed' : 'Mark Complete'}
            </Button1>
          </Box>
        </Tooltip>
      </div>
    </div>
  );
};

DefaultActions.defaultProps = {
  className: '',
  fileLinkCollection: null,
  uploadProgress: null,
  completed: false,
  hasAccounts: null,
  selectedAccountId: null,
};

DefaultActions.propTypes = {
  setProgress: PropTypes.func.isRequired,
  setFileLinkCollection: PropTypes.func.isRequired,
  fileLinkCollection: PropTypes.object,
  uploadProgress: PropTypes.number,
  showMarkCompleteTooltip: PropTypes.bool.isRequired,
  completed: PropTypes.bool,
  hasAccounts: PropTypes.bool,
  toggleHide: PropTypes.func.isRequired,
  selectedAccountId: PropTypes.number,
  markAsComplete: PropTypes.func.isRequired,
  setSelectedAccountId: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(DefaultActions);
